import { action, observable } from 'mobx';
import Api from '../../api';
import Store from '../../domain/entities/Store/Store';
import { ICoordinates, INavigatorPosition } from '../../interfaces/ICoordinates';
import normalizeApiErrors from '../../helpers/normalizeApiErrors';
import NotificationsCollection from '../../domain/entities/Notification/NotificationsCollection';
import Address, { IAddressAttributes } from '../../domain/entities/Address';
import { BOPIS_NOTIFICATION } from '../../constants/Notifications';
import { IStoreInfo } from '../../api/IDataMappings';
import StoresCollection from '../../domain/entities/Store/StoresCollection';
import GoogleMapsApi from '../../api/GoogleMapsApi';
import Config from '../../domain/entities/Config';
import { FIELD_TYPE } from '../../constants/Constants';

export interface IStoresRepositoryConfig {
  api: Api;
  config: Config;
  notificationsCollection: NotificationsCollection;
  googleMapsApi: GoogleMapsApi;
}

export interface ILocation {
  city?: string;
  state?: string | number | boolean;
  zip?: string | number | boolean;
}

export default class StoresRepository {
  @observable isBopisGiftWrapRemoved: boolean;

  @observable isInventoryLoading = true;

  @observable maxRadius: string;

  @observable config: Config;

  protected api: Api;

  protected notificationsCollection: NotificationsCollection;

  protected storesCollection: StoresCollection;

  protected googleMapsApi: GoogleMapsApi;

  constructor({
    api,
    config,
    notificationsCollection,
    googleMapsApi,
  }: IStoresRepositoryConfig) {
    this.config = config;

    this.api = api;

    this.notificationsCollection = notificationsCollection;

    this.storesCollection = new StoresCollection();

    this.googleMapsApi = googleMapsApi;
  }

  getStoresByGeolocation = async (coords: ICoordinates, isClickAndCollect = false, storeType = 0): Promise<void> => {
    try {
      this.setIsInventoryLoading(true);
      const response = await this.api.getBopisStoresByGeolocation(coords, isClickAndCollect, storeType);
      const stores = response.stores.map(store => Store.fromJSON(store));
      this.setIsInventoryLoading(false);
      this.storesCollection.setStores(stores);
      this.setMaxRadius(response.maxRadius);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      this.setIsInventoryLoading(false);
    }
  };

  removeUnavailableItems = async (doorId: string): Promise<void> => {
    try {
      const response = await this.api.removeItemsUnavailableForStore(doorId);
      if (response.notifications) {
        this.isBopisGiftWrapRemoved = response.notifications.filter(
          item => item.key === BOPIS_NOTIFICATION.GIFT_WRAP_REMOVED,
        ).length > 0;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  getStoresByAddress = async (location: Address | string | IAddressAttributes): Promise<void> => {
    const locationObj: ILocation = {};
    // Location parameter will be zip input for guest or new address
    //  or full address on saved address select.
    if (location instanceof Address) {
      locationObj.city = location.attributes[FIELD_TYPE.CITY];
      locationObj.state = location.attributes[FIELD_TYPE.STATE];
      locationObj.zip = location.attributes[FIELD_TYPE.ZIPCODE];
    } else if (typeof location === 'string') {
      locationObj.zip = location;
    } else {
      locationObj.zip = location[FIELD_TYPE.ZIPCODE];
    }
    try {
      this.setIsInventoryLoading(true);
      const { stores } = await this.api.getCourierStoresByAddress(locationObj);
      const mappedStores = stores.map((store: IStoreInfo) => Store.fromJSON(store));
      this.setIsInventoryLoading(false);
      this.storesCollection.setStores(mappedStores);
    } catch (e) {
      this.setIsInventoryLoading(false);
      const normalizedErrors = normalizeApiErrors(e);
      this.notificationsCollection.addNotifications(normalizedErrors);
    }
  };

  getStoresByIp = async (): Promise<void> => {
    try {
      this.setIsInventoryLoading(true);
      const { stores } = await this.api.getCourierStoresByIP();
      const mappedStores = stores.map((store: IStoreInfo) => Store.fromJSON(store));
      this.setIsInventoryLoading(false);
      this.storesCollection.setStores(mappedStores);
    } catch (e) {
      this.setIsInventoryLoading(false);
      // User can get stores by browser location or address if this call fails.
    }
  };

  getClickAndCollectStoresByIp = async (storeType: number): Promise<void> => {
    try {
      this.setIsInventoryLoading(true);
      const response = await this.api.getClickAndCollectStoresByIP(storeType);
      const mappedStores = response.stores.map((store: IStoreInfo) => Store.fromJSON(store));
      this.setIsInventoryLoading(false);
      this.storesCollection.setStores(mappedStores);
      this.setMaxRadius(response.maxRadius);
    } catch (e) {
      this.setIsInventoryLoading(false);
    }
  };

  private getBrowserCurrentPosition = (): Promise<INavigatorPosition> => (
    new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject);
    }));

  loadStoresByUserLocation = async (isClickAndCollect = false, storeType = 0): Promise<void> => {
    if (isClickAndCollect) {
      await this.getClickAndCollectStoresByIp(storeType);
    } else {
      await this.getStoresByIp();
    }
  };

  @action
  setIsInventoryLoading = (success: boolean): void => {
    this.isInventoryLoading = success;
  };

  getCoordinatesFromAddress = async (address: Address | string | IAddressAttributes): Promise<void> => {
    let searchAddress: string;
    if (address instanceof Address) {
      const {
        attributes: {
          [FIELD_TYPE.FIRST_LINE_ADDRESS]: firstLineAddress,
          [FIELD_TYPE.CITY]: city,
          [FIELD_TYPE.STATE]: state,
          [FIELD_TYPE.ZIPCODE]: zipcode,
        },
      } = address;
      searchAddress = `${firstLineAddress} ${city} ${state} ${zipcode}`;
    } else if (typeof address === 'string') {
      searchAddress = address as string;
    } else if (address[FIELD_TYPE.FIRST_LINE_ADDRESS] && address[FIELD_TYPE.ZIPCODE]) {
      searchAddress = `${address[FIELD_TYPE.FIRST_LINE_ADDRESS]} ${address[FIELD_TYPE.ZIPCODE]}`;
    }

    try {
      const { location } = await this.googleMapsApi.getCoordinatesFromAddress(searchAddress);

      if (location) {
        await this.getStoresByGeolocation(location);
      } else {
        await this.getStoresByAddress(address);
      }
    } catch (e) {
      await this.getStoresByAddress(address);
    }
  };

  @action
  setMaxRadius = (value: string): void => {
    this.maxRadius = value;
  };
}
